import React from 'react';
import { Users, Map, BookOpen, Bot, Cpu, Activity } from 'lucide-react';

const INK = 'var(--text-primary)';
const STEEL = 'var(--text-secondary)';
const RED = '#FF8618';

const ROADMAP = [
  {
    id: 1,
    Icon: Users,
    statusLabel: 'CONCEPT DEVELOPMENT',
    title: 'NEW CHARACTERS',
    description:
      'Fresh commanders and elite units join the galactic war. Each new character brings unique abilities, signature gear, and a distinct combat doctrine.',
    statusText: 'IN DEVELOPMENT',
    statusColor: '#FF8618',
  },
  {
    id: 2,
    Icon: Map,
    statusLabel: 'PLANNED EXPANSION',
    title: 'NEW MAPS',
    description:
      'Explore new battlegrounds across uncharted worlds. Steamreach. Aqua Rift. Warforge. Sunreach. Each one a distinct biome with its own rules.',
    statusText: 'IN PLANNING',
    statusColor: '#FF8618',
  },
  {
    id: 3,
    Icon: BookOpen,
    statusLabel: 'NARRATIVE PHASE',
    title: 'CHARACTER CHRONICLES',
    description:
      'Dive into the untold stories of the galaxy. Deep character lore, cinematic backstories, and evolving campaigns that reveal the war behind the war.',
    statusText: 'IN WRITING',
    statusColor: '#FF8618',
  },
  {
    id: 4,
    Icon: Bot,
    statusLabel: 'RESEARCH PHASE',
    title: 'AI ASSISTANT',
    description:
      'An intelligent in-game companion that guides strategy, adapts to your playstyle, and offers real-time tactical advice as the battle unfolds.',
    statusText: 'EARLY R&D',
    statusColor: '#C6530D',
  },
];

const FutureExpansion = () => {
  return (
    <section
      id="future"
      data-testid="future-section"
      className="relative py-16 px-6 overflow-hidden"
    >
      {/* Soft warm blooms */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 18% 14%, rgba(208,158,98,0.10) 0%, transparent 55%), radial-gradient(ellipse 60% 50% at 86% 90%, rgba(255,134,24,0.06) 0%, transparent 55%)',
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
          maskImage: 'radial-gradient(ellipse 90% 70% at 50% 40%, black 0%, transparent 85%)',
          WebkitMaskImage: 'radial-gradient(ellipse 90% 70% at 50% 40%, black 0%, transparent 85%)',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* SYSTEM UPDATE PROTOCOL pill */}
        <div className="flex justify-center mb-8">
          <div
            className="inline-flex items-center gap-3 px-4 py-2 rounded-full"
            style={{ background: 'rgba(20,27,34,0.8)', border: '1px solid rgba(255,134,24,0.45)', boxShadow: '0 6px 18px rgba(0,0,0,0.4)' }}
            data-testid="future-protocol-tag"
          >
            <span className="block w-2 h-2 rounded-full" style={{ background: RED, boxShadow: '0 0 8px rgba(255,134,24,0.6)' }} />
            <Cpu className="w-3.5 h-3.5" style={{ color: RED }} />
            <span className="text-[10px] font-bold tracking-[0.4em] uppercase" style={{ fontFamily: '"Black Ops One", sans-serif', color: RED }}>
              System Update Protocol
            </span>
            <span className="block w-2 h-2 rounded-full" style={{ background: RED, boxShadow: '0 0 8px rgba(255,134,24,0.6)' }} />
          </div>
        </div>

        {/* Title */}
        <h2
          className="font-black uppercase tracking-[0.04em] leading-[0.95] text-center"
          style={{ fontFamily: '"Black Ops One", sans-serif', fontSize: 'clamp(2.6rem, 7vw, 5.2rem)' }}
          data-testid="future-title"
        >
          <span style={{ color: INK, textShadow: '0 1px 0 rgba(255,255,255,0.7)' }}>FUTURE </span>
          <span style={{ color: RED, textShadow: '0 0 26px rgba(255, 134, 24, 0.28)' }}>EXPANSION</span>
        </h2>

        <p className="mt-5 max-w-3xl mx-auto text-center text-sm md:text-base" style={{ color: STEEL, fontFamily: '"Rajdhani", sans-serif', fontWeight: 500 }}>
          The galactic war is only the beginning. New characters, new battlegrounds, deeper stories, and intelligent assistance are coming in future expansions.
        </p>

        {/* Diamond divider */}
        <div className="flex justify-center my-7">
          <div className="w-3 h-3" style={{ background: RED, transform: 'rotate(45deg)', boxShadow: '0 0 12px rgba(255, 134, 24, 0.5)' }} />
        </div>

        {/* ROADMAP FEATURES */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-8 sm:gap-y-0 max-w-6xl mx-auto">
          {ROADMAP.map((card) => (
            <RoadmapCard key={card.id} card={card} />
          ))}
        </div>

        {/* Bottom banner */}
        <div
          className="mt-14 relative flex flex-col items-center justify-center text-center py-10 px-6"
          data-testid="future-banner"
        >
          {/* subtle vignette for readability only — no panel */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse 70% 80% at center, rgba(3,7,11,0.35) 0%, transparent 75%)' }}
          />

          <h3
            className="font-black uppercase tracking-[0.06em]"
            style={{
              fontFamily: '"Black Ops One", sans-serif',
              fontSize: 'clamp(1.6rem, 3.5vw, 2.6rem)',
              background: 'linear-gradient(180deg, #f2f4f6 0%, #FF8618 70%, #F07310 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', color: 'transparent',
            }}
          >
            The War Is Only Beginning
          </h3>

          <div className="my-4 flex items-center gap-3">
            <span className="block h-px w-12" style={{ background: 'rgba(255,134,24,0.5)' }} />
            <img src="/galwar-logo.png" alt="GAL WAR CC" className="w-7 h-7 object-contain" />
            <span className="block h-px w-12" style={{ background: 'rgba(255,134,24,0.5)' }} />
          </div>

          <p className="text-xs sm:text-sm font-bold tracking-[0.4em] uppercase" style={{ fontFamily: '"Black Ops One", sans-serif', color: 'var(--text-muted)' }}>
            New Worlds · New Stories · New Systems Incoming
          </p>
        </div>
      </div>
    </section>
  );
};

const RoadmapCard = ({ card }) => {
  const { Icon, statusLabel, title, description, statusText, statusColor } = card;
  return (
    <div
      className="feature-expansion-item flex flex-col items-center"
      data-testid={`roadmap-card-${card.id}`}
    >
      {/* Icon */}
      <Icon className="feature-expansion-icon w-8 h-8 mb-4" style={{ color: RED }} />

      {/* Title */}
      <h4 className="text-xl font-black uppercase tracking-[0.04em] mb-3" style={{ fontFamily: '"Black Ops One", sans-serif', color: INK }}>
        {title}
      </h4>

      {/* Description */}
      <p className="text-xs leading-relaxed mb-5" style={{ color: STEEL, fontFamily: 'Inter, sans-serif' }}>
        {description}
      </p>

      {/* Category label */}
      <p className="text-[9px] font-bold tracking-[0.3em] uppercase mb-3" style={{ fontFamily: '"Black Ops One", sans-serif', color: RED }}>
        {statusLabel}
      </p>

      {/* STATUS line */}
      <div className="mt-auto flex items-center justify-center gap-2">
        <Activity className="w-3 h-3" style={{ color: statusColor }} />
        <span className="text-[10px] font-bold tracking-[0.3em] uppercase" style={{ fontFamily: '"Black Ops One", sans-serif', color: 'var(--text-muted)' }}>
          Status
        </span>
        <span className="block w-1 h-1 rounded-full" style={{ background: statusColor, boxShadow: `0 0 6px ${statusColor}` }} />
        <span className="text-[10px] font-bold tracking-[0.25em] uppercase" style={{ fontFamily: '"Black Ops One", sans-serif', color: statusColor }}>
          {statusText}
        </span>
      </div>
    </div>
  );
};

export default FutureExpansion;
